import React, { useState } from 'react';
import { Plus, Filter, Eye, Edit3, Trash2, User, ChevronLeft, ChevronRight, Search, X } from 'lucide-react';

const EmployeeView = React.memo(({ 
  filteredEmployees, 
  loading, 
  viewState, 
  filterState, 
  modalHandlers, 
  employeeHandlers, 
  navigationHandlers,
  formatDate 
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');

  // Search logic
  const searchedEmployees = React.useMemo(() => {
    if (!searchQuery.trim()) return filteredEmployees;
    const query = searchQuery.toLowerCase();
    return filteredEmployees.filter(emp => {
      const name = (emp.name || '').toLowerCase();
      const email = (emp.email || '').toLowerCase();
      const phone = (emp.phone || '').toLowerCase();
      const position = (emp.position || emp.Position || '').toLowerCase();
      const dept = (emp.department || '').toLowerCase();
      const code = (emp.EmployeeCode || emp.employeeCode || '').toLowerCase();
      return name.includes(query) || 
             email.includes(query) || 
             phone.includes(query) || 
             position.includes(query) || 
             dept.includes(query) ||
             code.includes(query);
    });
  }, [filteredEmployees, searchQuery]);

  // Pagination logic
  const totalPages = Math.ceil(searchedEmployees.length / rowsPerPage);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = searchedEmployees.slice(indexOfFirstRow, indexOfLastRow);

  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  // Reset to page 1 if filtered or searched results change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchedEmployees.length]);

  return (
    <div className="mts-employee-table">
      <div className="mts-employee-table-header-container">
        <h2 className="mts-employee-table-title">Employee Directory ({searchedEmployees.length})</h2>
        
        {/* Search Bar */}
        <div className="mts-search-bar-container" style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          flex: '1',
          maxWidth: '380px',
          margin: '0 20px'
        }}>
          <Search size={16} style={{
            position: 'absolute',
            left: '12px',
            color: '#9ca3af',
            pointerEvents: 'none'
          }} />
          <input
            type="text"
            placeholder="Search by name, email, phone, position..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '0.625rem 2.25rem 0.625rem 2.25rem',
              fontSize: '0.875rem',
              color: '#1f2937',
              backgroundColor: '#f9fafb',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              outline: 'none',
              transition: 'all 0.2s ease-in-out',
              boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)'
            }}
            onFocus={(e) => {
              e.target.style.backgroundColor = '#ffffff';
              e.target.style.borderColor = '#10b981';
              e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.15)';
            }}
            onBlur={(e) => {
              e.target.style.backgroundColor = '#f9fafb';
              e.target.style.borderColor = '#d1d5db';
              e.target.style.boxShadow = 'inset 0 1px 2px rgba(0, 0, 0, 0.05)';
            }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{
                position: 'absolute',
                right: '10px',
                background: 'none',
                border: 'none',
                color: '#9ca3af',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '4px',
                borderRadius: '50%'
              }}
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div style={{ display: 'inline-flex', gap: '12px', alignItems: 'center' }}>
          <button 
            onClick={modalHandlers.toggleFilterSidebar}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '0.75rem 1rem',
              backgroundColor: viewState.filterSidebarOpen ? '#10b981' : '#f3f4f6',
              color: viewState.filterSidebarOpen ? 'white' : '#6b7280',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
          >
            <Filter size={16} />
            Filters
            {(filterState.statuses.length + filterState.states.length > 0) && (
              <span style={{
                backgroundColor: '#ef4444',
                color: 'white',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 'bold',
                marginLeft: '4px'
              }}>
                {filterState.statuses.length + filterState.states.length}
              </span>
            )}
          </button>
          <button 
            onClick={employeeHandlers.handleAddEmployee}
            className="mts-add-employee-btn"
          >
            <Plus size={16} />
            Add Employee
          </button>
        </div>
      </div>
      
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading employees...</p>
        </div>
      ) : filteredEmployees.length > 0 ? (
        <>
          <div className="mts-employee-table-table-header">
            <div>Name</div>
            <div>Email</div>
            <div>Mobile Number</div>
            {/* <div>Department</div> */}
            <div>Position</div>
            <div>Employment Details</div>
            <div style={{ textAlign: 'center' }}>Status</div>
            <div style={{ textAlign: 'center' }}>Actions</div>
          </div>
          
          <div className="mts-employee-table-table-rows">
            {currentRows.map(employee => (
              <div key={employee.id} className="mts-employee-table-table-row">
                <div>
                  <button
                    onClick={() => navigationHandlers.handleEmployeeClick(employee)}
                    className="mts-employee-name-btn"
                  >
                    {employee.name}
                  </button>
                </div>
                <div className="mts-contact-email">{employee.email}</div>
                <div className="mts-contact-phone">{employee.phone || 'N/A'}</div>
                {/* <div>{employee.department}</div> */}
                <div>{employee.position || employee.Position || 'N/A'}</div>
                <div>
                  <div className="mts-employment-type">{employee.employmentType}</div>
                  <div className="mts-employment-details">
                    Code: {employee.EmployeeCode || employee.employeeCode || 'N/A'} | Hired: {formatDate(employee.hireDate)}
                  </div>
                  {/* <div className="mts-timesheet-required-badge" style={{ 
                    fontSize: '11px', 
                    fontWeight: '600',
                    color: employee.TimesheetRequired ? '#059669' : '#dc2626',
                    marginTop: '4px'
                  }}>
                    Timesheet: {employee.TimesheetRequired ? 'Required' : 'Not Required'}
                  </div> */}
                </div>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <span className={`mts-status-badge ${
                      employee.status === 'Active' ? 'mts-status-registered' : 'mts-status-pending'
                    }`}
                    style={{
                      display: 'inline-flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '2px',
                      width: 'fit-content', padding: '4px 12px'
                    }}>
                      <span style={{ fontWeight: '600', fontSize: '14px' }}>
                        {employee.status}
                      </span>
                      {employee.StatusChangedAt && (
                        <span style={{
                          fontSize: '11px',
                          opacity: 0.85,
                          fontWeight: '500'
                        }}>
                          {new Date(employee.StatusChangedAt).toLocaleDateString()}
                        </span>
                      )}
                    </span>
                </div>
                <div className="mts-actions" style={{ justifyContent: 'center' }}>
                  {/* <button 
                    className="mts-action-btn view"
                    onClick={() => employeeHandlers.handleViewEmployee(employee)}
                    title="View Employee Details"
                  >
                    <Eye size={14} />
                  </button> */}
                  <button 
                    className="mts-action-btn"
                    onClick={() => employeeHandlers.handleEditEmployee(employee)}
                    title="Edit Employee"
                  >
                    <Edit3 size={14} />
                  </button>
                  <button 
                    className="mts-action-btn delete"
                    onClick={() => employeeHandlers.handleDeleteEmployee(employee.id)}
                    title="Delete Employee"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {searchedEmployees.length > 0 && (
            <div
              className="mts-pagination"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '1.2rem 2rem',
                borderTop: '1px solid #f1f5f9',
                backgroundColor: '#ffffff',
                flexWrap: 'wrap',
                gap: '12px'
              }}
            >
              {/* Left: Per page dropdown */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b', fontSize: '0.84rem' }}>
                <span>Rows per page:</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: '5px 10px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    backgroundColor: '#ffffff',
                    fontSize: '0.84rem',
                    color: '#374151',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span style={{ marginLeft: '8px', color: '#64748b' }}>
                  Showing {searchedEmployees.length > 0 ? indexOfFirstRow + 1 : 0}–{Math.min(indexOfLastRow, searchedEmployees.length)} of {searchedEmployees.length}
                </span>
              </div>

              {/* Right: Pagination controls */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button 
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  className="mts-pagination-btn"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  <ChevronLeft size={16} />
                  Previous
                </button>
                
                <div className="mts-pagination-info" style={{ fontSize: '0.84rem', color: '#64748b' }}>
                  Page {currentPage} of {totalPages}
                </div>

                <button 
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className="mts-pagination-btn"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                  }}
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="no-data">
          <User size={48} />
          <p>
            {searchQuery.trim() 
              ? `No employees match your search "${searchQuery}"`
              : (filterState.statuses.length > 0 || filterState.states.length > 0 
                ? 'No employees match the selected filters' 
                : 'No employees found')
            }
          </p>
          {searchQuery.trim() ? (
            <button 
              onClick={() => setSearchQuery('')}
              className="mts-add-employee-btn"
              style={{ backgroundColor: '#6b7280' }}
            >
              Clear Search
            </button>
          ) : (
            <button 
              onClick={employeeHandlers.handleAddEmployee}
              className="mts-add-employee-btn"
            >
              <Plus size={16} />
              Add Employee
            </button>
          )}
        </div>
      )}
    </div>
  );
});

EmployeeView.displayName = 'EmployeeView';

export default EmployeeView;
